import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import auctionService from '../services/auctionService';
import { formatCurrency, daysRemaining, formatDate } from '../utils/validators';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import './Dashboard.css';

const Dashboard = () => {
  const [activeAuctions, setActiveAuctions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const [auctionsRes, analyticsRes, performanceRes] = await Promise.all([
        auctionService.getSellerAuctions('active'),
        auctionService.getAnalytics(),
        auctionService.getPerformance(),
      ]);

      if (auctionsRes.success) {
        setActiveAuctions(auctionsRes.data.auctions || []);
      }
      if (analyticsRes.success) {
        setAnalytics(analyticsRes.data);
      }
      if (performanceRes.success) {
        setPerformance(performanceRes.data);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="container py-4"><div className="spinner"><div className="loading"></div>Loading...</div></div>;
  }

  return (
    <div className="container py-4">
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Quick Stats */}
      <div className="grid grid-4">
        <div className="stat-card">
          <div className="stat-label">Active Auctions</div>
          <div className="stat-value">{activeAuctions.length}</div>
          <Link to="/auctions?status=active" className="stat-link">View all →</Link>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Sales</div>
          <div className="stat-value">{formatCurrency(analytics?.totalSales || 0)}</div>
          <div className="stat-subtitle">This month</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Sale Price</div>
          <div className="stat-value">{formatCurrency(analytics?.avgPrice || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sell-Through Rate</div>
          <div className="stat-value">{analytics?.sellThroughRate || 0}%</div>
        </div>
      </div>

      {/* Recent Auctions */}
      <div className="card mt-3">
        <div className="card-header">
          <h2 className="card-title">Active Auctions</h2>
          <Link to="/list-vehicle" className="btn btn-primary btn-small">
            + List Vehicle
          </Link>
        </div>

        {activeAuctions.length === 0 ? (
          <div className="empty-state">
            <p>No active auctions</p>
            <Link to="/list-vehicle" className="btn btn-primary">
              Create your first auction
            </Link>
          </div>
        ) : (
          <div className="auction-list">
            {activeAuctions.slice(0, 5).map((auction) => (
              <div key={auction.id} className="auction-item">
                <div className="auction-header">
                  <div>
                    <h3>{auction.vehicle.year} {auction.vehicle.make} {auction.vehicle.model}</h3>
                    <p className="text-muted">{auction.vehicle.vin}</p>
                  </div>
                  <div className="auction-status">
                    <span className="badge badge-success">{auction.status}</span>
                  </div>
                </div>
                <div className="auction-details grid grid-4">
                  <div>
                    <small>Current Bid</small>
                    <p className="font-bold">{formatCurrency(auction.currentBid || auction.reservePrice)}</p>
                  </div>
                  <div>
                    <small>Bids</small>
                    <p className="font-bold">{auction.bidCount || 0}</p>
                  </div>
                  <div>
                    <small>Time Left</small>
                    <p className="font-bold">{daysRemaining(auction.endTime)}</p>
                  </div>
                  <div>
                    <Link to={`/auction/${auction.id}`} className="btn btn-secondary btn-small">
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Charts */}
      {analytics?.chartData && (
        <div className="grid grid-2 mt-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Sales Trend</h3>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#2563eb" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Auction Status</h3>
            </div>
            {performance && (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[
                  { name: 'Completed', value: performance.completedCount },
                  { name: 'Active', value: performance.activeCount },
                  { name: 'Paused', value: performance.pausedCount },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="card mt-3">
        <h3 className="card-title mb-2">Quick Actions</h3>
        <div className="grid grid-4">
          <Link to="/list-vehicle" className="quick-link">
            <span>🚗</span>
            <div>
              <strong>List Vehicle</strong>
              <p>Create new auction</p>
            </div>
          </Link>
          <Link to="/auctions" className="quick-link">
            <span>📊</span>
            <div>
              <strong>Manage Auctions</strong>
              <p>View all auctions</p>
            </div>
          </Link>
          <Link to="/payouts" className="quick-link">
            <span>💰</span>
            <div>
              <strong>Payouts</strong>
              <p>Settlement history</p>
            </div>
          </Link>
          <Link to="/analytics" className="quick-link">
            <span>📈</span>
            <div>
              <strong>Analytics</strong>
              <p>Performance metrics</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
